import "styles/modal.scss";
import "components/Documents/drawer/DocumentsDrawer.scss";

import cx from "classnames";
import Button from "components/Button/Button";
import KaboodleToaster from "components/common/KaboodleToaster";
import DocumentTable from "components/Documents/table/DocumentTable";
import LoadingIcon from "components/Loading/LoadingIcon";
import SearchBar from "components/SearchBar/SearchBar";
import { PAGE_SIZE } from "constants/TableConstants";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { setShowClientDocumentsDrawer } from "reducers/drawer";
import {
  useGetDocumentsByClientIdQuery,
  useBulkDownloadDocumentsMutation,
} from "services/documents-api";
import { useQuery } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

const ClientDocumentsDrawer = () => {
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId: string }>();
  const [page, setPage] = useState(1);
  const query = useQuery();

  const showClientDocumentsDrawer = useSelector(
    (state) => state.drawer.showClientDocumentsDrawer,
  );

  const {
    data: documents = { results: [], count: 0 },
    isFetching,
    isLoading,
  } = useGetDocumentsByClientIdQuery(
    {
      clientId: clientId!,
      searchTerm: query.get(GLOBAL_SEARCH_PARAM) || undefined,
      sortBy: "-id",
      limit: PAGE_SIZE.toString(),
      offset: ((page - 1) * PAGE_SIZE).toString(),
    },
    {
      skip: !clientId || !showClientDocumentsDrawer,
    },
  );

  const [bulkDownloadDocuments, { isLoading: isBulkDownloading }] =
    useBulkDownloadDocumentsMutation();

  const handleBulkDownload = async () => {
    if (!clientId || documents.results.length === 0 || isBulkDownloading)
      return;

    try {
      await bulkDownloadDocuments({
        clientId: parseInt(clientId),
      }).unwrap();

      KaboodleToaster.success(
        "Bulk download request submitted. You will receive an email when your download is ready.",
      );
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "data" in error
          ? (error.data as { error?: string })?.error ||
            "Failed to submit bulk download request"
          : "Failed to submit bulk download request";

      KaboodleToaster.error(errorMessage);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="documents--Loading">
          <LoadingIcon message="Loading Documents" />
        </div>
      );
    }

    if (!documents?.results?.length) {
      return (
        <div className="empty-state">
          <div className="empty-state-content">
            <span className="material-icons">description</span>
            <h3>
              {query.get(GLOBAL_SEARCH_PARAM)
                ? `No Documents Found`
                : `No Documents Available`}
            </h3>
            <p>No documents are available for this client</p>
          </div>
        </div>
      );
    }

    return (
      <DocumentTable
        documents={documents.results}
        totalCount={documents.count}
        page={page}
        onPageChange={setPage}
        isLoading={isFetching}
        isDrawer={showClientDocumentsDrawer}
        onDelete={() => {}}
      />
    );
  };

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showClientDocumentsDrawer,
      })}
    >
      <div className="documents-wrapper w-search">
        <div className="header-primary flex jc-sb ai-c">
          Client Documents
          <div className="header-actions" style={{ display: "flex" }}>
            {documents.results.length > 0 && (
              <Button
                className="upload-btn-small download-all-btn"
                onClick={handleBulkDownload}
                disabled={isBulkDownloading || isLoading}
              >
                <span className="material-icons">
                  {isBulkDownloading ? "hourglass_empty" : "download"}
                </span>
                {isBulkDownloading ? "Requesting..." : "Download All"}
              </Button>
            )}
            <a
              className="header-primary alt-btn"
              onClick={() => dispatch(setShowClientDocumentsDrawer(false))}
            >
              Close
            </a>
          </div>
        </div>

        <div className="search-container">
          <SearchBar
            override={true}
            variant="documents"
            filterType="documents"
          />
        </div>

        <div className="data-values mrg-top">{renderContent()}</div>
      </div>
    </div>
  );
};

export default ClientDocumentsDrawer;
